<template>
  <transition name="fade">
    <div v-if="props.show" class="select-auto">
      <div
        class="absolute w-screen h-screen bg-black opacity-70 inset-0 z-10"
        @click.stop="onModalToggle"
      />

      <ModalStartDesigningPanel
        v-if="activeContent == 'galleryOrScratch'"
        @close="onModalToggle"
        @gallery="activeContent = 'galleryList'"
        @scratch="activeContent = 'patternList'"
      />

      <!-- Mobile pattern picker (Figma 4832:137202) — replaces the legacy 2-col grid on small screens -->
      <ModalMobileGridOptionsModal
        v-if="activeContent == 'patternList' && isMobile"
        :show="true"
        :selected-pattern-id="selectedPattern?.id ?? null"
        mode="scratch"
        :hide-close="false"
        @close="onModalToggle"
        @select="onMobilePatternPicked"
      />

      <div
        v-if="activeContent == 'patternList' && !isMobile"
        class="absolute inset-0 w-screen h-screen flex items-center justify-center z-10"
      >
        <div
          class="w-[95%] sm:w-[90%] max-w-[763px] p-4 sm:p-8 relative mx-auto my-auto shadow-lg bg-white rounded-[10px] z-10 max-h-[95vh] flex flex-col sm:block overflow-hidden sm:overflow-y-auto"
        >
          <button
            aria-label="Close modal"
            class="absolute top-2 right-2 sm:top-4 sm:right-4 w-12 h-12 sm:w-8 sm:h-8 flex items-center justify-center text-black hover:text-gray-600 transition-colors z-50"
            @click="onModalToggle"
          >
            <svg
              class="w-7 h-7 sm:w-5 sm:h-5"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
              stroke-width="2.5"
            >
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                d="M6 18L18 6M6 6l12 12"
              />
            </svg>
          </button>

          <div class="text-center shrink-0">
            <button
              v-if="!options?.showGridWarning"
              aria-label="Go back"
              class="absolute left-2 top-2 sm:left-4 sm:top-4 flex items-center text-black hover:text-gray-600 transition-colors z-50 w-12 h-12 sm:w-auto sm:h-auto justify-center sm:justify-start"
              @click="activeContent = 'galleryOrScratch'"
            >
              <svg
                class="w-7 h-7 sm:w-4 sm:h-4 sm:mr-1"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
                stroke-width="2.5"
              >
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  d="M15 19l-7-7 7-7"
                />
              </svg>
            </button>

            <h2
              class="text-2xl sm:text-[32px] font-bold text-black mb-2 px-12 sm:px-0"
            >
              Choose Your Grid
            </h2>
            <p
              class="text-xs sm:text-sm font-semibold text-black mb-4 sm:mb-8 px-4"
            >
              Select a grid! This will shape how your design comes together.
            </p>
          </div>

          <!-- Scrollable grid area (mobile) / normal flow (desktop) -->
          <div
            ref="gridScrollRef"
            class="relative flex-1 min-h-0 overflow-y-auto sm:overflow-visible px-2 sm:px-4"
            @scroll="onGridScroll"
          >
            <div
              class="grid grid-cols-2 sm:grid-cols-3 gap-4 sm:gap-x-10 sm:gap-y-6 mb-3 sm:mb-4"
            >
              <button
                v-for="pattern in filteredGridPatternList"
                :key="pattern.id"
                class="flex flex-col items-center cursor-pointer group relative"
                :class="{ 'opacity-50 pointer-events-none': !pattern.active }"
                @click="selectedPattern = pattern"
              >
                <div
                  v-if="pattern.id === 'square'"
                  class="absolute top-[60%] sm:top-[65%] left-1/2 transform -translate-x-1/2 bg-[#313d58] text-white text-[9px] sm:text-[11px] font-semibold px-3 sm:px-5 py-1 sm:py-2 rounded-full z-10 whitespace-nowrap"
                >
                  most popular
                </div>

                <div
                  class="bg-[#f7f6f5] rounded-[5px] w-full aspect-square max-w-[210px] flex items-center justify-center mb-2 sm:mb-4 group-hover:bg-[#eeeded] transition-colors overflow-hidden"
                  :class="{
                    'ring-2 ring-black': selectedPattern?.id === pattern.id,
                  }"
                >
                  <img
                    :src="getPatternIcon(pattern.id)"
                    :alt="pattern.label"
                    :class="getPatternIconClass(pattern.id)"
                  />
                </div>

                <h3 class="text-sm sm:text-[16px] font-bold text-black">
                  {{ pattern.label }}
                </h3>
              </button>

              <!-- Canvas Dimensions (desktop only — inside grid flow) -->
              <div class="hidden sm:flex col-span-2 w-full flex-col">
                <div
                  class="flex flex-col flex-grow text-center py-2 border-2 border-black mb-4"
                >
                  <h2 class="text-lg font-bold text-black mb-3">
                    Canvas Dimensions
                  </h2>
                  <div
                    class="flex flex-row flex-wrap justify-center gap-3 px-2"
                  >
                    <div
                      class="flex bg-gray-200 py-2 px-4 rounded items-center justify-center"
                    >
                      <div class="min-w-[56px] font-semibold text-sm">
                        Width
                      </div>
                      <input
                        v-model="gridWidthFt"
                        class="w-12 h-8 mx-1 text-center text-sm"
                        type="number"
                        min="0"
                      />
                      <span class="text-sm">ft</span>
                      <input
                        v-model="gridWidthIn"
                        class="w-12 h-8 mx-1 text-center text-sm"
                        type="number"
                        min="0"
                      />
                      <span class="text-sm">in</span>
                    </div>
                    <div
                      class="flex bg-gray-200 py-2 px-4 rounded items-center justify-center"
                    >
                      <div class="min-w-[56px] font-semibold text-sm">
                        Height
                      </div>
                      <input
                        v-model="gridHeightFt"
                        class="w-12 h-8 mx-1 text-center text-sm"
                        type="number"
                        min="0"
                      />
                      <span class="text-sm">ft</span>
                      <input
                        v-model="gridHeightIn"
                        class="w-12 h-8 mx-1 text-center text-sm"
                        type="number"
                        min="0"
                      />
                      <span class="text-sm">in</span>
                    </div>
                  </div>
                </div>
                <h3 class="text-sm text-[16px] font-bold text-black">&nbsp;</h3>
              </div>
            </div>

            <!-- Scroll indicator (mobile only) -->
            <div
              v-if="showScrollIndicator"
              class="sticky bottom-0 left-0 right-0 sm:hidden pointer-events-none transition-opacity duration-300"
            >
              <div class="h-12 bg-gradient-to-t from-white to-transparent" />
              <div class="bg-white flex items-center justify-center pb-1 gap-1">
                <svg
                  class="w-4 h-4 text-gray-400 animate-bounce"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                  stroke-width="2"
                >
                  <path
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    d="M19 9l-7 7-7-7"
                  />
                </svg>
                <span class="text-xs text-gray-400">Scroll for more</span>
              </div>
            </div>
          </div>

          <!-- Sticky footer: dimensions + start (mobile) / normal flow (desktop) -->
          <div
            class="shrink-0 text-center pt-3 sm:pt-0 border-t border-gray-200 sm:border-0"
          >
            <!-- Canvas Dimensions (mobile only — in sticky footer) -->
            <div class="sm:hidden w-full flex flex-col mb-2">
              <div
                class="flex flex-col flex-grow text-center py-2 border-2 border-black"
              >
                <h2 class="text-base font-bold text-black mb-3">
                  Canvas Dimensions
                </h2>
                <div class="flex flex-col flex-wrap justify-center gap-2 px-2">
                  <div
                    class="flex bg-gray-200 py-2 px-3 rounded items-center justify-center"
                  >
                    <div class="min-w-[50px] font-semibold text-sm">Width</div>
                    <input
                      v-model="gridWidthFt"
                      class="w-10 h-8 mx-1 text-center text-sm"
                      type="number"
                      min="0"
                    />
                    <span class="text-sm">ft</span>
                    <input
                      v-model="gridWidthIn"
                      class="w-10 h-8 mx-1 text-center text-sm"
                      type="number"
                      min="0"
                    />
                    <span class="text-sm">in</span>
                  </div>
                  <div
                    class="flex bg-gray-200 py-2 px-3 rounded items-center justify-center"
                  >
                    <div class="min-w-[50px] font-semibold text-sm">Height</div>
                    <input
                      v-model="gridHeightFt"
                      class="w-10 h-8 mx-1 text-center text-sm"
                      type="number"
                      min="0"
                    />
                    <span class="text-sm">ft</span>
                    <input
                      v-model="gridHeightIn"
                      class="w-10 h-8 mx-1 text-center text-sm"
                      type="number"
                      min="0"
                    />
                    <span class="text-sm">in</span>
                  </div>
                </div>
              </div>
            </div>

            <div class="sm:text-center mb-1 sm:mb-0">
              <button
                class="px-8 py-3 rounded-full font-semibold transition-colors min-h-[44px] min-w-[120px]"
                :class="
                  selectedPattern
                    ? 'bg-black text-white hover:bg-gray-800'
                    : 'bg-gray-300 text-gray-500 cursor-not-allowed'
                "
                :disabled="!selectedPattern"
                @click="handleStartDesign"
              >
                Start
              </button>
            </div>
          </div>
        </div>
      </div>

      <ModalGalleryListPanel
        v-if="activeContent == 'galleryList'"
        @close="onModalToggle"
        @back="activeContent = 'galleryOrScratch'"
      />

      <div
        v-if="activeContent == 'changeGridWarning'"
        class="absolute inset-0 w-screen h-screen flex items-center justify-center"
      >
        <div
          class="w-screen mx-2 xl:min-w-[400px] xl:max-w-xl p-3 relative xl:mx-auto my-auto shadow-lg bg-white z-10"
        >
          <div>
            <div class="text-center p-3 flex-auto justify-center leading-6">
              <h2 class="text-xl font-bold text-[#646483]">
                Changing the grid will reset the studio
              </h2>
              <div
                class="flex flex-wrap space-around justify-center max-w-[240px] mx-auto"
              >
                Be sure to save any work before continuing.
              </div>
            </div>
            <div class="p-3 text-center space-x-4 md:block">
              <button
                aria-label="Go back"
                class="mb-2 md:mb-0 bg-[#646483] px-5 py-2 tracking-wider text-white hover:bg-gray-800 rounded-full"
                @click="handleWarningGoBack"
              >
                Go Back
              </button>
              <button
                aria-label="Continue without saving"
                class="mb-2 md:mb-0 bg-white px-5 py-2 tracking-wider text-black rounded-full border-2 border-black"
                @click="handleWarningContinue"
              >
                Continue Without Saving
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  </transition>
</template>

<script setup>
import {
  FOOT_IN_PIXELS,
  GRID_PATTERN_IDS,
  GRID_PATTERN_LIST,
  INCH_IN_PIXELS,
  SELECTABLE_TOOLS,
} from "~/composables/grid/constants";
import { GRID_MENU } from "~/composables/shapes/constants";

const gridStore = useGridStore();
const projectStore = useProjectStore();
const loadingStore = useLoadingStore();
const modalStore = useModalStore();

const filteredGridPatternList = computed(() => GRID_PATTERN_LIST);
const options = computed(() => modalStore.getModalOptions(ModalType.New));

const activeContent = ref(options.value?.activeContent || "galleryOrScratch");
const selectedPattern = ref(null);
const gridWidthFt = ref(gridStore.gridSize.width.ft);
const gridWidthIn = ref(gridStore.gridSize.width.in);
const gridHeightFt = ref(gridStore.gridSize.height.ft);
const gridHeightIn = ref(gridStore.gridSize.height.in);

const { isMobile } = useDeviceDetection();

const gridScrollRef = ref(null);
const showScrollIndicator = ref(true);

const emit = defineEmits(["modalToggle"]);
const props = defineProps({
  show: {
    type: Boolean,
    default: false,
  },
});

const onGridScroll = () => {
  const el = gridScrollRef.value;
  if (!el) return;
  const threshold = 20;
  showScrollIndicator.value =
    el.scrollHeight - el.scrollTop - el.clientHeight > threshold;
};

const startDesignFromScratch = () => {
  loadingStore.setLoading(true);

  const gridRef = projectStore.gridRef;
  gridStore.$reset();
  projectStore.$reset();
  projectStore.setGridRef(gridRef);
  useRouter().replace({ query: {} });
  deleteCookie("studio_design_id");

  gridStore.selectedTool = isMobile.value
    ? SELECTABLE_TOOLS.MOVE_GRID
    : SELECTABLE_TOOLS.ADD;

  gridStore.setGridPattern(selectedPattern.value);
  gridStore.setSelectedShape(GRID_MENU[selectedPattern.value.id].default);
  gridStore.gridWidth =
    gridWidthFt.value * FOOT_IN_PIXELS + gridWidthIn.value * INCH_IN_PIXELS;
  gridStore.gridHeight =
    gridHeightFt.value * FOOT_IN_PIXELS + gridHeightIn.value * INCH_IN_PIXELS;

  gridStore.setTempItemByCurrentSelection();

  loadingStore.setLoading(false);
  onModalToggle();
};

const onModalToggle = () => {
  emit("modalToggle");
  resetModalChoices();
};

const resetModalChoices = () => {
  activeContent.value = "galleryOrScratch";
  selectedPattern.value = null;
};

const getPatternIcon = (patternId) => {
  const patternIcons = {
    square: "/img/pattern-square-filled.svg",
    "45-deg": "/img/pattern-45-degree-with-background.svg",
    freeform: "/img/pattern-freeform-final.svg",
    hex: "/img/pattern/design-pattern-hex.webp",
    "hex-spaced": "/img/pattern/design-pattern-spaced-hex.webp",
    "hex-90": "/img/pattern/design-pattern-90deg-hex.webp",
    "3d": "/img/pattern/design-pattern-3d.webp",
  };
  return patternIcons[patternId] || "/img/pattern-square-filled.svg";
};

const getPatternIconClass = (patternId) => {
  const patternIcons = {
    [`${GRID_PATTERN_IDS.SQUARE}`]: "w-full h-full scale-[1.2]",
    [`${GRID_PATTERN_IDS.SQUARE_45}`]:
      "w-full h-full p-8 rotate-45 scale-[2.5] translate-y-10",
    [`${GRID_PATTERN_IDS.FREEFORM}`]: "w-full h-full p-4",
    [`${GRID_PATTERN_IDS.HEX}`]: "w-full h-full p-4 scale-[1.2]",
    [`${GRID_PATTERN_IDS.HEX_SPACED}`]: "w-full h-full p-4 scale-[1.2]",
    [`${GRID_PATTERN_IDS.HEX_90}`]: "w-full h-full p-4 scale-[1.2]",
    [`${GRID_PATTERN_IDS.THREE_D}`]: "w-full h-full",
  };
  return patternIcons[patternId] || "w-full h-full p-4";
};

const handleWarningGoBack = () => {
  activeContent.value = "patternList";
};

const handleStartDesign = () => {
  if (options.value?.showGridWarning) {
    activeContent.value = "changeGridWarning";
  } else {
    startDesignFromScratch();
  }
};

const handleWarningContinue = () => {
  startDesignFromScratch();
};

// Mobile pattern picker (MobileGridOptionsModal) emits this when the user
// picks a grid. We mirror the desktop behavior: stash the selection, then
// either show the grid-change warning or kick off the start flow.
const onMobilePatternPicked = (pattern) => {
  selectedPattern.value = pattern;
  if (options.value?.showGridWarning) {
    activeContent.value = "changeGridWarning";
  } else {
    startDesignFromScratch();
  }
};
</script>

<style>
.fade-enter,
.fade-leave-to {
  opacity: 0;
}

.fade-enter-active,
.fade-leave-active {
  transition: opacity 300ms ease-out;
}
</style>
